import type { ArrangementStatus } from '../schema/arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHAT AN OFFICER IS TOLD ABOUT THE STATE OF THEIR DUTY. [P5b §5]
 *
 *  ⚠️ NO CLIENT MAY RENDER `status` DIRECTLY, AND THE TWO REASONS ARE BOTH MEASURED.
 *
 *  `ISSUED` is not a status and never has been — the circulated contract invented it, and
 *  `docs/api/officer-contract.md` §0 records that as the largest of its eight errors. And
 *  naming the first officer moves a published order to `ALLOCATION_IN_PROGRESS`
 *  (`markWorkBegun`), so a duty an officer can actually SEE is usually already off
 *  `PUBLISHED`. A client branching on `=== 'PUBLISHED'` is wrong most of the time, and a
 *  client printing the raw value tells a constable their duty is "allocation in progress",
 *  which is a sentence about a nodal officer's workflow rather than about their evening.
 *
 *  ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THIS FILE HOLDS THE DERIVATION. IT DOES NOT HOLD THE WORDS, AND THAT IS THE DESIGN.
 *
 *  Two rules pull against each other here and both are kept:
 *
 *   • `web/src/lib/labels.ts` states that display labels live on the CLIENT because "the
 *     API's enum values are the contract, and a label change must never be able to alter
 *     one". English in `packages/shared` would put a word where a contract belongs.
 *   • The PWA sends every user-facing string through `t()` against a catalogue, because
 *     Bengali is a second file and will be asked for (PA_REQUIREMENTS §7). A hardcoded
 *     English string imported from shared is untranslatable by construction.
 *
 *  So what is shared is the REDUCTION — six statuses to five officer-facing states — and
 *  what is per-client is the wording. Both apps key their catalogue on the state, so they
 *  cannot disagree about WHICH thing is being said; the canonical English is written down
 *  once, in the contract document's §4, so they do not disagree about the words either.
 *  `pwa`'s `i18n.test.ts` asserts its catalogue covers every member of the tuple below,
 *  so a state added here without words is a failing test rather than a blank badge.
 *
 *  ⚠️ NO TONE HERE EITHER. `fillState.constant.ts` already settled that colour is a client
 *  concern — "`web/src/lib/labels.ts` is the only place they acquire a colour" — and a
 *  status hue in a shared package would be the second answer to that question.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OFFICER_DUTY_STATES = ['CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NOT_ISSUED'] as const;

export type OfficerDutyState = (typeof OFFICER_DUTY_STATES)[number];

/**
 * ⚠️ TOTAL OVER THE STATUS ENUM, AND `DRAFT` GETS ITS OWN ANSWER RATHER THAN A SAFE ONE.
 *
 * A draft cannot reach this surface: publish is what materialises the assignments, and
 * un-publish DELETES them rather than editing them, so there is no path by which a caller
 * holds an assignment on a `DRAFT` order. Mapping it to `CONFIRMED` "because it cannot
 * happen" would mean that if it ever did, an officer would be told to report for an order
 * nobody has issued. `NOT_ISSUED` says the true thing, and costs one tuple member.
 *
 * ⚠️ [amended P5c] `ACTIVE` AND `CLOSED` NOW HAVE WRITERS, AND NOT ONE LINE HERE CHANGED.
 * They were mapped ahead of use for the reason `assignments` got its duty window in P5a —
 * doing it later means a second pass over every client that renders a badge — and the
 * first `START_DUTY` on a duty now moves both the deployment and its order to `ACTIVE`,
 * with the lifecycle job closing them. Both clients rendered the new states correctly the
 * day the writers landed, which is the test of the decision to map them early.
 */
export const officerDutyStateOf = (status: ArrangementStatus): OfficerDutyState => {
  switch (status) {
    /*
     * ⚠️ TWO STATUSES, ONE ANSWER, DELIBERATELY. The difference between them is whether a
     * NODAL officer has started naming people, which is not a fact about the constable's
     * duty. Both mean "you are on this, it is going ahead".
     *
     * ⚠️ THE COMMENT SITS ABOVE BOTH LABELS RATHER THAN BETWEEN THEM. A comment between two
     * case labels reads to `no-fallthrough` as a case with a BODY and no `break`, so the
     * empty-group form stops being recognised as intentional. Same code, one position over.
     */
    case 'PUBLISHED':
    case 'ALLOCATION_IN_PROGRESS':
      return 'CONFIRMED';
    case 'ACTIVE':
      return 'IN_PROGRESS';
    case 'CLOSED':
      return 'COMPLETED';
    /** Returned deliberately rather than hidden: the officer was told to report. */
    case 'CANCELLED':
      return 'CANCELLED';
    case 'DRAFT':
      return 'NOT_ISSUED';
  }
};
